import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { TabsContent } from '../../../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { Label } from '../../../../components/ui/label';
import { CsvBatchesPanel } from '../../../../components/CsvBatchesPanel';
import { InvestorPicker } from '../../../../components/InvestorPicker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Upload,
  Download,
  FileSpreadsheet,
  Info,
  Inbox,
  CheckCircle2,
  XCircle,
  Trash2,
  Link2,
  Edit2,
  RefreshCw,
} from 'lucide-react';
import { API_URL, TRANSACTION_TYPES, getTransactionConfig } from '../utils/formatters';

export function ImportCsvTabPanel({ ctx }) {
  const {
    handleDownloadTemplate,
    importFile, setImportFile,
    setImportResult,
    importForce, setImportForce,
    handleImport,
    importing,
    importResult,
    headers,
    // Import & Approve
    batches, batchesLoading,
    selectedBatch, setSelectedBatch,
    batchDetail, batchDetailLoading,
    showCompletedBatches, setShowCompletedBatches,
    fetchBatches, fetchBatchDetail,
    handleApproveBatch, handleRejectBatch, handleDeleteBatch,
    handleApproveItem, handleRejectItem,
    linkDialog, setLinkDialog,
    linkInvestorId, setLinkInvestorId,
    handleLinkInvestor,
    editingStaging, setEditingStaging,
    editStagingType, setEditStagingType,
    editStagingAmount, setEditStagingAmount,
    editStagingDate, setEditStagingDate,
    editStagingNotes, setEditStagingNotes,
    handleEditStaging, handleSaveEditStaging,
    selectedImport, toggleSelectImport, toggleSelectAllImport,
    setBulkEditOpen, setBulkEditTarget,
    reconInvestors,
  } = ctx;

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const investorOptions = (reconInvestors || []).map(i => ({
    id: i.id, name: i.name || i.entity_name || i.email || i.id, rp_code: i.rp_code || i.risk_participant_code || '',
  }));

  const detailRows = batchDetail?.records || [];
  const hasUnclassified = detailRows.some(r => r.import_status === 'pending' && r.transaction_type === 'unclassified');
  const pendingRows = detailRows.filter(r => r.import_status === 'pending');

  return (
    <TabsContent value="import" className="space-y-6" data-testid="import-tab">
      {/* Upload Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" />Upload Transaction CSV</CardTitle>
            <CardDescription>Upload a CSV using the RiskParticipant code from Table_RP. Rows are committed directly. Deal flows (purchases / repayments) are NOT uploaded here — they come from the RP / LB sheets of the Excel loanbook import.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={handleDownloadTemplate} className="w-full" data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-2" />Download Template CSV
            </Button>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <FileSpreadsheet className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={e => { setImportFile(e.target.files?.[0]||null); setImportResult(null); }} className="max-w-xs mx-auto" data-testid="import-file-input" />
              {importFile && <p className="text-sm text-gray-600 mt-2">{importFile.name}</p>}
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none" title="Skip the (investor + date + type + amount + reference + loan_id) dedup check. Every row in the file will be inserted, even if it duplicates an existing row. Use ONLY when you deliberately want to force-submit rows that would otherwise be flagged as duplicates.">
              <input
                type="checkbox"
                checked={importForce}
                onChange={e => setImportForce(e.target.checked)}
                className="rounded"
                data-testid="import-force-checkbox"
              />
              <span className={importForce ? 'text-red-700 font-semibold' : 'text-slate-600'}>
                Force submit (bypass dedupe — inserts every row even if it looks like a duplicate)
              </span>
            </label>
            <div className="flex gap-2">
              <Button
                onClick={() => handleImport(true)}
                disabled={importing || !importFile}
                variant="outline"
                className="flex-1"
                data-testid="import-dry-run-btn"
                title="Parse + dedup-check without writing to the DB"
              >
                {importing ? '…' : 'Dry-Run Preview'}
              </Button>
              <Button
                onClick={() => handleImport(false)}
                disabled={importing || !importFile}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="import-btn"
              >
                {importing ? 'Processing...' : 'Upload & Commit'}
              </Button>
            </div>
            {importResult && (
              <div className={`p-3 ${importResult.dry_run ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'} border rounded-lg text-sm space-y-1`}>
                <p className={`font-medium ${importResult.dry_run ? 'text-amber-900' : 'text-blue-800'}`}>
                  {importResult.dry_run ? '👁 Dry-Run Preview (NOTHING was written)' : 'Import Summary'}
                </p>
                <p className="text-green-700">{importResult.dry_run ? 'Would commit' : 'Committed'}: <strong>{importResult.committed_count ?? importResult.staged_count ?? 0}</strong> records</p>
                {(importResult.skipped_duplicates ?? 0) > 0 && (
                  <p className="text-slate-700">Silently skipped as duplicates: <strong>{importResult.skipped_duplicates}</strong></p>
                )}
                {(importResult.unmatched_investors ?? importResult.unmatched_count ?? 0) > 0 && <p className="text-amber-700">Unmatched investor codes: <strong>{importResult.unmatched_investors ?? importResult.unmatched_count}</strong></p>}
                {importResult.error_count > 0 && (
                  <>
                    <p className="text-red-700 font-medium">Row errors: <strong>{importResult.error_count}</strong></p>
                    {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
                      <div className="mt-2 max-h-64 overflow-y-auto border border-red-200 bg-red-50 rounded p-2 text-xs" data-testid="import-errors-list">
                        <ul className="space-y-0.5 font-mono">
                          {importResult.errors.slice(0, 50).map((e, i) => (
                            <li key={i} className="text-red-700">
                              <span className="font-semibold">Row {e.row}:</span> {e.error}
                            </li>
                          ))}
                        </ul>
                        {importResult.error_count > importResult.errors.length && (
                          <p className="mt-1 text-red-600 italic">…showing first {importResult.errors.length} of {importResult.error_count} errors</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Info className="h-4 w-4" />Template Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-gray-50 rounded font-mono text-xs break-all">
                risk_participant, transaction_date, transaction_type, amount, reference, notes, loan_id
              </div>
              <Table>
                <TableHeader><TableRow><TableHead className="text-xs">Field</TableHead><TableHead className="text-xs">Description</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">risk_participant</TableCell><TableCell className="text-xs text-gray-600">RiskParticipant code from Table_RP (e.g. 01-01)</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">transaction_date</TableCell><TableCell className="text-xs text-gray-600">YYYY-MM-DD format</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">transaction_type</TableCell><TableCell className="text-xs text-gray-600">investment / withdrawal / fee_mgmt / fee_perf / fee_rebate / bank_interest / adjustment / write_off</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">amount</TableCell><TableCell className="text-xs text-gray-600">Commas, spaces, currency prefix, and accounting parens for negatives are all accepted. For fee_mgmt / fee_perf this is EX-VAT.</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">loan_id</TableCell><TableCell className="text-xs text-gray-600">Optional — links to specific loan</TableCell></TableRow>
                </TableBody>
              </Table>
              <div className="p-2 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-800">
                Rows are committed directly. Deal flows (deal_purchase / deal_repayment) are ingested from the RP / LB sheets of the Excel loanbook upload — NOT from this CSV.
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs space-y-1">
                <p className="font-semibold text-slate-700 mb-1">Transaction Type Guide:</p>
                <p><span className="font-mono bg-green-50 px-1 rounded">investment</span> — New capital deposited by the investor</p>
                <p><span className="font-mono bg-blue-50 px-1 rounded">bank_interest</span> — Interest earned on the bank balance</p>
                <p><span className="font-mono bg-amber-50 px-1 rounded">withdrawal</span> — Capital withdrawn by the investor</p>
                <p><span className="font-mono bg-stone-100 px-1 rounded">fee_mgmt</span> — Management fee, EX-VAT (15% VAT auto-added on import)</p>
                <p><span className="font-mono bg-zinc-100 px-1 rounded">fee_perf</span> — Performance fee, EX-VAT (15% VAT auto-added on import)</p>
                <p><span className="font-mono bg-teal-100 px-1 rounded">fee_rebate</span> — Fee refund from advisor (INCL-VAT). Positive amount → REDUCES net fees, inflow to investor.</p>
                <p><span className="font-mono bg-slate-100 px-1 rounded">adjustment</span> — Signed manual correction (positive = cash IN, negative = cash OUT)</p>
                <p><span className="font-mono bg-orange-50 px-1 rounded">write_off</span> — Non-cash: bad debt write-off, rounding correction</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Import & Approve — staged bank-sync / CSV rows awaiting classification */}
      <Card data-testid="import-approve-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Inbox className="h-4 w-4" />Import &amp; Approve</CardTitle>
              <CardDescription>Staged rows (bank syncs and CSV batches) not yet committed to Transactions. Classify each row, then approve.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={showCompletedBatches} onChange={e => setShowCompletedBatches(e.target.checked)} className="rounded" data-testid="show-completed-batches-checkbox" />
                <span className="text-slate-600">Show completed batches</span>
              </label>
              <Button variant="outline" size="sm" onClick={fetchBatches} disabled={batchesLoading} data-testid="refresh-batches-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {batchesLoading ? (
            <p className="text-sm text-slate-400 py-4 text-center">Loading batches…</p>
          ) : batches.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No pending import batches.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Unmatched</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map(b => (
                  <TableRow
                    key={b.batch_id}
                    className={`cursor-pointer ${selectedBatch === b.batch_id ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                    onClick={() => fetchBatchDetail(b.batch_id)}
                    data-testid={`import-batch-row-${b.batch_id}`}
                  >
                    <TableCell className="font-medium text-sm max-w-[220px] truncate" title={b.batch_name}>{b.batch_name || b.batch_id}</TableCell>
                    <TableCell className="text-sm">{b.created_at ? new Date(b.created_at).toLocaleDateString('en-ZA') : '-'}</TableCell>
                    <TableCell className="text-right">{b.total}</TableCell>
                    <TableCell className="text-right">{b.pending}</TableCell>
                    <TableCell className="text-right">{b.unmatched > 0 ? <span className="text-amber-700 font-medium">{b.unmatched}</span> : 0}</TableCell>
                    <TableCell className="text-right font-mono text-sm">R {(b.total_amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <Badge className={
                        b.status === 'approved' ? 'bg-green-100 text-green-700' :
                        b.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>{b.status}</Badge>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {b.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" title="Reject entire batch" onClick={() => handleRejectBatch(b.batch_id)} data-testid={`reject-batch-${b.batch_id}`}>
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" title="Delete entire batch (permanent)" onClick={() => handleDeleteBatch(b.batch_id)} data-testid={`delete-batch-${b.batch_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Batch detail — rows to classify/approve */}
          {selectedBatch && (
            <div className="border-t pt-4 mt-2">
              {batchDetailLoading ? (
                <p className="text-sm text-slate-400 py-4 text-center">Loading batch detail…</p>
              ) : batchDetail ? (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h4 className="text-sm font-semibold text-slate-700">{batchDetail.batch_name} — {batchDetail.pending} pending of {batchDetail.total}</h4>
                    <div className="flex items-center gap-2">
                      {selectedImport.size > 0 && (
                        <Button size="sm" variant="outline" onClick={() => { setBulkEditTarget('staging'); setBulkEditOpen(true); }} data-testid="bulk-classify-staging-btn">
                          <Edit2 className="h-3.5 w-3.5 mr-1" />Classify {selectedImport.size} Selected
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={pendingRows.length === 0}
                        onClick={() => {
                          if (hasUnclassified && !window.confirm(
                            `${detailRows.filter(r => r.transaction_type === 'unclassified').length} row(s) are still unclassified — approving now will commit them with type "unclassified". Classify them first instead?\n\nClick OK to approve anyway (not recommended), Cancel to go back and classify first.`
                          )) return;
                          if (!hasUnclassified && !window.confirm(`Approve ${pendingRows.length} pending row(s) in this batch? This commits them to Transactions.`)) return;
                          handleApproveBatch(batchDetail.batch_id);
                        }}
                        data-testid="approve-batch-btn"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve Batch
                      </Button>
                    </div>
                  </div>
                  {hasUnclassified && (
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      Some rows below are still "unclassified" — pick a real type for each (Edit) before approving, otherwise they'll commit as "unclassified".
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">
                          <input type="checkbox" checked={pendingRows.length > 0 && selectedImport.size === pendingRows.length} onChange={() => toggleSelectAllImport(pendingRows.map(r => r.id))} data-testid="select-all-staging" />
                        </TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Investor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Match</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailRows.map(r => {
                        const cfg = getTransactionConfig(r.transaction_type);
                        const isPending = r.import_status === 'pending';
                        return (
                          <TableRow key={r.id} data-testid={`staging-row-${r.id}`}>
                            <TableCell>
                              {isPending && (
                                <input type="checkbox" checked={selectedImport.has(r.id)} onChange={() => toggleSelectImport(r.id)} data-testid={`select-staging-${r.id}`} />
                              )}
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{r.transaction_date}</TableCell>
                            <TableCell className="text-sm">{r.investor_name || r.risk_participant || <span className="text-amber-600">unmatched</span>}</TableCell>
                            <TableCell><Badge className={cfg.color}>{cfg.label}</Badge></TableCell>
                            <TableCell className="text-sm max-w-[280px] truncate" title={r.notes || r.reference}>{r.notes || r.reference || '-'}</TableCell>
                            <TableCell className={`text-right font-mono text-sm ${r.amount < 0 ? 'text-red-600' : 'text-green-700'}`}>R {Number(r.amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell>
                              <Badge className={r.match_status === 'unmatched' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>{r.match_status}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                r.import_status === 'approved' ? 'bg-green-100 text-green-700' :
                                r.import_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                r.import_status === 'skipped_duplicate' ? 'bg-slate-100 text-slate-600' :
                                'bg-amber-100 text-amber-700'
                              }>{r.import_status}</Badge>
                            </TableCell>
                            <TableCell>
                              {isPending && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-7 px-2" title="Edit / classify" onClick={() => handleEditStaging(r)} data-testid={`edit-staging-${r.id}`}>
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  {r.match_status === 'unmatched' && (
                                    <Button size="sm" variant="ghost" className="h-7 px-2" title="Link to investor" onClick={() => setLinkDialog({ open: true, item: r })} data-testid={`link-staging-${r.id}`}>
                                      <Link2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:text-emerald-700" title="Approve this row" onClick={() => handleApproveItem(r.id)} data-testid={`approve-staging-${r.id}`}>
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" title="Reject this row" onClick={() => handleRejectItem(r.id)} data-testid={`reject-staging-${r.id}`}>
                                    <XCircle className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / classify staging row dialog */}
      <Dialog open={!!editingStaging} onOpenChange={(v) => { if (!v) setEditingStaging(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Classify staged transaction</DialogTitle>
            <DialogDescription>Pick the real transaction type (and correct amount/date/notes if needed) before approving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Transaction type *</Label>
              <Select value={editStagingType} onValueChange={setEditStagingType}>
                <SelectTrigger data-testid="edit-staging-type-select"><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent>
                  {TRANSACTION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Amount</Label>
              <Input type="number" step="0.01" value={editStagingAmount} onChange={e => setEditStagingAmount(e.target.value)} data-testid="edit-staging-amount" />
            </div>
            <div className="space-y-1">
              <Label>Date</Label>
              <Input type="date" value={editStagingDate} onChange={e => setEditStagingDate(e.target.value)} data-testid="edit-staging-date" />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={editStagingNotes} onChange={e => setEditStagingNotes(e.target.value)} data-testid="edit-staging-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStaging(null)}>Cancel</Button>
            <Button onClick={handleSaveEditStaging} data-testid="save-staging-edit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link investor dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(v) => { if (!v) setLinkDialog({ open: false, item: null }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link to investor</DialogTitle>
            <DialogDescription>This staged row has no matched investor — pick one to link it to.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <InvestorPicker
              value={linkInvestorId}
              onChange={setLinkInvestorId}
              investors={investorOptions}
              includeAll={false}
              placeholder="Select investor…"
              className="w-full"
              testId="link-investor-picker"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleLinkInvestor} disabled={!linkInvestorId} data-testid="confirm-link-investor-btn">Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import History — undo bad uploads */}
      <CsvBatchesPanel API_URL={API_URL} headers={headers} />
    </TabsContent>
  );
}
